import React from 'react';
import { Container, Box, CircularProgress, Typography, Link } from '@mui/material';
import SEOHead from '../components/SEOHead';
import { useTermsContent } from '../hooks/useTermsContent';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, documentLegalPaperSx } from '../constants/documentTheme';
import { useTranslation } from 'react-i18next';

const Terms = () => {
  const { t } = useTranslation();
  const { content, loading, error } = useTermsContent('user');

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Terms of Service - Déjà Vu Nomad"
        description="Terms of Service for Déjà Vu Nomad travel planning platform. Read our user agreement, pricing, refund policy, and legal terms."
        keywords="terms of service, user agreement, travel planning, pricing, refund policy, legal terms"
        canonical="https://dejavunomad.com/terms"
      />
      <DocumentMarketingHero
        eyebrow={t('home.footer.legal')}
        title="Terms of Service"
        subtitle="Déjà Vu Nomad — travel planning platform user agreement."
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={documentLegalPaperSx}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="error" paragraph>
                Failed to load terms content.
              </Typography>
              <Link href="/terms-user.html" target="_blank" rel="noopener">
                View Terms in new tab
              </Link>
            </Box>
          ) : (
            <Box 
              sx={{ 
                '& h2': { 
                  color: DOCUMENT.oxblood, 
                  fontFamily: DOCUMENT.display,
                  fontSize: '1.5rem',
                  fontWeight: 400,
                  mt: 4,
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${DOCUMENT.rule}`
                },
                '& h3': {
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mt: 2,
                  mb: 1
                },
                '& p': {
                  mb: 2,
                  lineHeight: 1.7
                },
                '& ul': {
                  pl: 3,
                  mb: 2
                },
                '& li': {
                  mb: 1
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 3
                },
                '& th': {
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  p: 1.5,
                  textAlign: 'left'
                },
                '& td': {
                  p: 1.5,
                  borderBottom: '1px solid #ddd'
                },
                '& tr:nth-of-type(even)': {
                  bgcolor: '#f8f9fa'
                },
                '& .highlight-box': {
                  bgcolor: '#f8f9fa',
                  borderLeft: '4px solid #591716',
                  p: 3,
                  my: 3,
                  borderRadius: '0 8px 8px 0'
                },
                '& .warning-box': {
                  bgcolor: '#fff3cd',
                  borderLeft: '4px solid #ffc107',
                  p: 3,
                  my: 3,
                  borderRadius: '0 8px 8px 0'
                },
                '& .contact-box': {
                  bgcolor: '#e8f4f8',
                  p: 3,
                  borderRadius: 2,
                  mt: 3
                },
                '& .last-updated': {
                  color: '#666',
                  fontSize: '0.9rem',
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #eee'
                },
                '& a': {
                  color: DOCUMENT.oxblood
                }
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Terms;
